import { getApiV1ClientsExternalOptions } from "Infrastructure/Api/generated/@tanstack/react-query.gen";
import type { SabClientListItemDto } from "Infrastructure/Api/generated/types.gen";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useDebouncedValue } from "Modeling/Hooks/useDebouncedValue";
import { Search, User } from "Shared/appIcons";
import {
	clientDisplayName,
	sabClientIdentificationNumber,
} from "Shared/Client/selectedClientStorage";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const MIN_QUERY_LENGTH = 2;
const SEARCH_LIMIT = 20;

type SabClientPickerProps = {
	onPick: (client: SabClientListItemDto) => void;
};

export const SabClientPicker = ({ onPick }: SabClientPickerProps) => {
	const { t } = useResource();
	const [query, setQuery] = useState("");
	const debouncedQuery = useDebouncedValue(query, 300);

	const queryReady = debouncedQuery.trim().length >= MIN_QUERY_LENGTH;
	const { data, isFetching } = useQuery({
		...getApiV1ClientsExternalOptions({
			query: {
				Search: debouncedQuery.trim(),
				Offset: 0,
				Limit: SEARCH_LIMIT,
			},
		}),
		enabled: queryReady,
	});

	const clients = data?.clients ?? [];

	return (
		<div className="flex flex-col gap-3">
			<div className="relative">
				<Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
				<Input
					autoFocus
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder={t(Resources.Modeling.LaunchMode.SearchPlaceholder)}
					className="pl-9"
				/>
			</div>

			{!queryReady ? (
				<div className="text-small text-muted-foreground px-1">
					{t(Resources.Modeling.LaunchMode.MinCharsHint)}
				</div>
			) : isFetching ? (
				<div className="text-small text-muted-foreground px-1">
					{t(Resources.Modeling.LaunchMode.Searching)}
				</div>
			) : clients.length === 0 ? (
				<div className="text-small text-muted-foreground px-1">
					{t(Resources.Modeling.LaunchMode.NoResults)}
				</div>
			) : (
				<ul className="max-h-80 overflow-y-auto rounded-md border border-border">
					{clients.map((c) => (
						<li key={c.sabClientID}>
							<ClientRow client={c} onPick={onPick} />
						</li>
					))}
				</ul>
			)}
		</div>
	);
};

type ClientRowProps = {
	client: SabClientListItemDto;
	onPick: (client: SabClientListItemDto) => void;
};

const ClientRow = ({ client, onPick }: ClientRowProps) => {
	const name = clientDisplayName(client);
	const subtitle =
		client.personalIdentificationNumber ??
		client.companyIdentificationNumber ??
		"";
	return (
		<Button
			type="button"
			variant="ghost"
			onClick={() => onPick(client)}
			className="flex h-auto w-full items-center justify-start gap-3 rounded-none border-b border-border px-3 py-2.5 text-left last:border-b-0"
		>
			<div className="bg-primary/10 text-primary flex size-8 flex-shrink-0 items-center justify-center rounded-full">
				<User className="size-4" />
			</div>
			<div className="min-w-0 flex-1">
				<div className="text-small text-foreground truncate font-medium">
					{name}
				</div>
				{subtitle ? (
					<div className="text-tiny text-muted-foreground truncate">
						{subtitle}
					</div>
				) : null}
			</div>
		</Button>
	);
};

export const getSabClientIdentificationNumber = sabClientIdentificationNumber;
